import { apiClient } from '../lib/apiClient';

export interface TeamActiveRep {
  userId: string;
  email: string;
  name: string | null;
  role: 'sales_rep' | 'manager' | 'organization_admin' | 'super_admin';
  otherStationCount: number;
  lastSignInAt: string | null;
}

export interface TeamPendingInvite {
  id: string;
  email: string;
  invitedByEmail: string | null;
  invitedAt: string;
  expiresAt: string;
}

export interface TeamPageData {
  station: { id: string; slug: string; name: string };
  activeReps: TeamActiveRep[];
  pendingInvites: TeamPendingInvite[];
}

export type InviteOutcome =
  | 'invite_sent'
  | 'access_added'
  | 'already_has_access'
  | 'rejected_other_org'
  | 'rejected_invalid_email';

export interface InviteResult {
  email: string;
  status: InviteOutcome;
  inviteId?: string;
  targetUserId?: string;
  /** When set, the server has matched this email to an existing auth.users
   *  row. Drives role-aware copy in the result banner. */
  targetRole?: string;
  error?: string;
}

export interface InviteResultSummary {
  results: InviteResult[];
  counts: { sent: number; added: number; skipped: number; rejected: number };
}

export interface StationInviteView {
  stationId: string;
  stationName: string;
  stationSlug: string | null;
  publisherName: string;
  email: string;
  inviterEmail: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedAt: string;
  expiresAt: string;
}

export const stationTeamService = {
  listTeam: (slug: string) =>
    apiClient.get<TeamPageData>(`/stations/${encodeURIComponent(slug)}/team`),

  inviteReps: (slug: string, emails: string[]) =>
    apiClient.post<InviteResultSummary>(
      `/stations/${encodeURIComponent(slug)}/team/invite`,
      { emails },
    ),

  resendInvite: (slug: string, inviteId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/invites/${encodeURIComponent(inviteId)}/resend`,
    ),

  revokeInvite: (slug: string, inviteId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/invites/${encodeURIComponent(inviteId)}/revoke`,
    ),

  removeRep: (slug: string, userId: string) =>
    apiClient.post<{ ok: true }>(
      `/stations/${encodeURIComponent(slug)}/team/members/${encodeURIComponent(userId)}/remove`,
    ),

  lookupInvite: (token: string) =>
    apiClient.get<StationInviteView>(
      `/station-invites/${encodeURIComponent(token)}`,
      { anonymous: true },
    ),

  acceptInvite: (token: string) =>
    apiClient.post<
      | { ok: true; stationSlug: string; passwordRequired: boolean }
      | { ok: false; needsSignIn: true }
    >(`/station-invites/${encodeURIComponent(token)}/accept`, { token }),

  acceptInviteWithPassword: (token: string, password: string) =>
    apiClient.post<{ ok: true; email: string; stationSlug: string }>(
      `/station-invites/${encodeURIComponent(token)}/accept-with-password`,
      { token, password },
      { anonymous: true },
    ),
};
